import React from "react";
import { Badge, Button, Drawer, Empty, List, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listServerNotifications, markAllServerNotificationsRead } from "../../api";
import {
  clearNotifications,
  ingestServerNotifications,
  listNotifications,
  markAllNotificationsRead,
  subscribeNotifications,
  unreadNotificationCount,
  type AppNotification,
} from "../ux/notificationStore";

const POLL_MS = 20000;

export function NotificationCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<AppNotification[]>(() => listNotifications());
  const [unread, setUnread] = React.useState(() => unreadNotificationCount());

  const refreshLocal = React.useCallback(() => {
    setItems(listNotifications());
    setUnread(unreadNotificationCount());
  }, []);

  React.useEffect(() => subscribeNotifications(refreshLocal), [refreshLocal]);

  // US-507 / US-1006: poll notification-center and merge into local feed
  React.useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const rows = await listServerNotifications();
        if (!cancelled && rows.length) {
          ingestServerNotifications(rows);
        }
      } catch {
        // offline / service down — keep localStorage feed
      }
    };
    void pull();
    const id = window.setInterval(() => void pull(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <>
      <Badge count={unread} size="small" overflowCount={9}>
        <Button
          type="text"
          icon={<BellOutlined />}
          aria-label={t("compliance.notifications.title")}
          data-testid="notification-bell"
          onClick={() => {
            setOpen(true);
            markAllNotificationsRead();
            setUnread(0);
            void markAllServerNotificationsRead().catch(() => undefined);
          }}
        />
      </Badge>
      <Drawer
        title={t("compliance.notifications.title")}
        open={open}
        onClose={() => setOpen(false)}
        width={360}
        extra={
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearNotifications();
              setItems([]);
            }}
          >
            {t("compliance.notifications.clear")}
          </Button>
        }
      >
        {items.length === 0 ? (
          <Empty description={t("compliance.notifications.empty")} />
        ) : (
          <List
            dataSource={items}
            renderItem={(item) => (
              <List.Item
                style={{ cursor: item.caseId ? "pointer" : undefined }}
                onClick={() => {
                  if (item.caseId) {
                    setOpen(false);
                    navigate(`/dds/cases/${item.caseId}`);
                  }
                }}
              >
                <List.Item.Meta
                  title={item.title}
                  description={
                    <>
                      {item.detail && (
                        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                          {item.detail}
                        </Typography.Paragraph>
                      )}
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(item.createdAt).toLocaleString()}
                        {item.source === "server" ? ` · ${t("compliance.notifications.fromServer")}` : ""}
                      </Typography.Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </>
  );
}
